import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Container,
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/system';
import {
  signInWithEmailAndPassword,
  getMultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
} from 'firebase/auth';
import { useAuth } from './contexts/AuthContext';
import { useFirebase } from './contexts/FirebaseContext';
import { useError } from './contexts/ErrorContext';

// Using the theme variants instead of custom styled components
const LoginForm = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255,255,255,0.15)',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  backdropFilter: 'blur(10px)',
  marginTop: theme.spacing(3),
  maxWidth: '500px',
  margin: '0 auto',
}));

const SignInEmail = () => {
  const { currentUser } = useAuth();
  const { auth } = useFirebase();
  const navigate = useNavigate();
  const location = useLocation();
  const returnUrl = location.state?.returnUrl || '/dashboard';
  const { logAuthenticationError, logValidationError } = useError();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Add new state variables for MFA flow
  const [showMFA, setShowMFA] = useState(false);
  const [verificationId, setVerificationId] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [resolver, setResolver] = useState(null);
  const [phoneHint, setPhoneHint] = useState('');
  const [recaptchaVerifier, setRecaptchaVerifier] = useState(null);

  // Initialize recaptcha verifier when needed
  useEffect(() => {
    if (showMFA && !recaptchaVerifier) {
      const verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
        callback: () => {
          console.log('reCAPTCHA verified');
        },
        'expired-callback': () => {
          console.log('reCAPTCHA expired');
          setError('Verification expired. Please try again.');
        },
      });

      setRecaptchaVerifier(verifier);
    }

    // Cleanup function
    return () => {
      if (recaptchaVerifier) {
        recaptchaVerifier.clear();
      }
    };
  }, [showMFA, auth, recaptchaVerifier]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      const errorMessage = 'Email and password are required';
      setError(errorMessage);
      
      // Log validation error
      await logValidationError(new Error(errorMessage), 'email_password', {
        has_email: !!email,
        has_password: !!password
      });
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await signInWithEmailAndPassword(auth, email, password);

      // Redirect to return URL after successful login
      navigate(returnUrl);
    } catch (error) {
      console.error('Error signing in:', error);

      // Handle MFA required case
      if (error.code === 'auth/multi-factor-auth-required') {
        // Get resolver
        const resolver = getMultiFactorResolver(auth, error);
        setResolver(resolver);

        // Get the phone hint if available
        const phoneHint =
          resolver.hints.find((hint) => hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID)
            ?.phoneNumber || '';

        setPhoneHint(phoneHint);
        setShowMFA(true);
        return;
      }

      // Handle specific Firebase auth errors and log them
      let errorMessage = 'Failed to sign in. Please try again later';
      
      switch (error.code) {
        case 'auth/invalid-email':
          errorMessage = 'Invalid email address format';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled';
          break;
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password';
          break;
        default:
          errorMessage = 'Failed to sign in. Please try again later';
      }
      
      setError(errorMessage);
      
      // Log authentication error with Firebase error code
      await logAuthenticationError(error, 'email_signin');
    } finally {
      setLoading(false);
    }
  };

  const sendVerificationCode = async () => {
    try {
      setSendingCode(true);
      setError(null);

      if (!resolver) {
        throw new Error('MFA resolver is missing');
      }

      // Send verification code using the invisible reCAPTCHA verifier
      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        { multiFactorHint: resolver.hints[0], session: resolver.session },
        recaptchaVerifier
      );

      setVerificationId(verificationId);
    } catch (error) {
      console.error('Error sending verification code:', error);

      if (error.code === 'auth/missing-recaptcha-token') {
        setError('reCAPTCHA verification failed. Please refresh and try again.');
      } else if (error.code === 'auth/quota-exceeded') {
        setError('SMS quota exceeded. Please try again later.');
      } else {
        setError(`Failed to send verification code: ${error.message}`);
      }
    } finally {
      setSendingCode(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();

    if (!verificationCode) {
      setError('Verification code is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (!resolver) {
        throw new Error('MFA resolver is missing');
      }

      // Create credential with the verification ID and code
      const cred = PhoneAuthProvider.credential(verificationId, verificationCode);

      // Complete sign in with multi-factor
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
      await resolver.resolveSignIn(multiFactorAssertion);

      // Reset MFA state
      setShowMFA(false);
      setVerificationId('');
      setVerificationCode('');
      setResolver(null);

      // Redirect to settings page after successful verification
      navigate('/settings');
    } catch (error) {
      console.error('Error verifying code:', error);

      if (error.code === 'auth/invalid-verification-code') {
        setError('Invalid verification code. Please try again.');
      } else {
        setError(`Failed to verify code: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Reset MFA flow
  const cancelMFA = () => {
    setShowMFA(false);
    setVerificationId('');
    setVerificationCode('');
    setResolver(null);
    setError(null);
  };

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        {currentUser && navigate(returnUrl)}

        {!currentUser && (
          <>
            {location.state?.message && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {location.state.message}
              </Alert>
            )}

            {!showMFA ? (
              <Box className="homeContainer">
                <LoginForm component="form" onSubmit={handleEmailLogin}>
                  <Typography variant="h5" gutterBottom align="center">
                    Continue with Email
                  </Typography>

                  {error && <Alert severity="error">{error}</Alert>}

                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    variant="outlined"
                    color="loginField"
                  />

                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    variant="outlined"
                    color="loginField"
                  />

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <Link
                      to="/forgot-password"
                      style={{ color: 'inherit', textDecoration: 'none' }}
                    >
                      <Typography variant="body2" color="loginLink">
                        Forgot Password?
                      </Typography>
                    </Link>
                  </Box>

                  <Button
                    type="submit"
                    fullWidth
                    variant="containedWagl"
                    color="primary"
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
                  </Button>

                  <Button
                    component={Link}
                    to="/register"
                    state={{ returnUrl: location.state?.returnUrl }}
                    variant="body2"
                  >
                    New user? Register here
                  </Button>
                </LoginForm>
              </Box>
            ) : (
              // MFA Verification UI
              <Paper
                variant="outlined"
                sx={{
                  p: 4,
                  mt: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  maxWidth: '500px',
                  mx: 'auto',
                }}
              >
                <Typography variant="h5" component="h1" gutterBottom>
                  Two-Factor Authentication
                </Typography>

                <Typography variant="body1" gutterBottom>
                  Please verify your identity with the second factor.
                </Typography>

                {phoneHint && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    We'll send a verification code to: {phoneHint}
                  </Typography>
                )}

                {error && (
                  <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                    {error}
                  </Alert>
                )}

                {/* Hidden reCAPTCHA container */}
                <div id="recaptcha-container" style={{ display: 'none' }}></div>

                {!verificationId ? (
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={sendVerificationCode}
                    disabled={sendingCode}
                    sx={{ mt: 2 }}
                  >
                    {sendingCode ? <CircularProgress size={24} /> : 'Send Verification Code'}
                  </Button>
                ) : (
                  <Box component="form" onSubmit={verifyCode} sx={{ width: '100%', mt: 2 }}>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      label="Verification Code"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      disabled={loading}
                    />

                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loading}
                      sx={{ mt: 2 }}
                    >
                      {loading ? <CircularProgress size={24} /> : 'Verify'}
                    </Button>
                  </Box>
                )}

                <Button
                  variant="text"
                  onClick={cancelMFA}
                  sx={{ mt: 2 }}
                  disabled={loading || sendingCode}
                >
                  Back to Login
                </Button>
              </Paper>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default SignInEmail;
